import {Button as AntdButton, message, Row, Divider, TourProps, Tour, Button} from 'antd'
import React, {useEffect, useRef, useState} from 'react'
import './todos.less'
import {useNavigate, useRevalidator, useRouteLoaderData} from 'react-router-dom'
import {ToDo, ToDoList} from '../../entities/todoList'
import {ToDoListWithToDos} from '../../services/ToDoService'
import {useIntl} from 'react-intl'
import {ToDoFilters, ToDoPageFilters, ToDoSortingField} from './ToDoPageFilters'
import {sortToDo} from './toDoSorting'
import {ToDoPageNewToDo} from './ToDoPageNewToDo'
import {ToDoPageToDoCardsList} from './ToDoPageToDoCardsList'
import {PlantModal} from '../plantForm/PlantModal'
import {ToDoModal} from './ToDoModal'
import {
  CustomJournalEventType,
  CustomToDoType,
  DefaultEventType,
  JournalEventType,
  ToDoType,
} from '../../entities/journalRecord'
import {PlantCollection} from '../../entities/plant'

export function removeFalsyKeys<T extends object>(obj: T): T {
  const copy = {...obj} as Record<string, unknown>
  for (const key of Object.keys(copy)) {
    if (!copy[key]) {
      delete copy[key]
    }
  }

  return copy as T
}

export function ToDoPage() {
  const {
    plantedPlantsNamesWithId = [],
    unfinishedToDos = [],
    userToDoTypes,
    userJournalEventTypes,
    userPlantCollections,
    userPlantCollectionsWithPlantsIds,
  } = useRouteLoaderData('todos') as {
    plantedPlantsNamesWithId: {id: number; name: string}[]
    unfinishedToDos: ToDo[]
    userToDoTypes: CustomToDoType[]
    userJournalEventTypes: CustomJournalEventType[]
    userPlantCollections: PlantCollection[]
    userPlantCollectionsWithPlantsIds: any
  }

  const intl = useIntl()
  const navigate = useNavigate()

  const [userToDos, setUserToDos] = useState<ToDo[]>(unfinishedToDos)

  const [filters, setFilters] = useState<ToDoFilters>({types: {}, plants: {}})
  const [toDoSortingField, setToDoSortingField] = useState<ToDoSortingField>('date')
  const [sortingDirection, setSortingDirection] = useState<boolean>(true)

  const filteredToDos = userToDos.filter((todo) => {
    //TODO добавить проверку, если все фильтры пустые, - равнозначно, что все выбрано?
    const startDate = todo.targetStartDateUtc ? new Date(todo.targetStartDateUtc) : null

    if (filters?.startDate) {
      if (!startDate || startDate < filters.startDate) {
        return false
      }
    }

    if (filters?.endDate) {
      if (!startDate || startDate > filters.endDate) {
        return false
      }
    }

    if (Object.keys(filters.types).length !== 0) {
      // read once so the union narrows across the branches
      const type = todo.type

      if (!type || !type.type) {
        return false
      } else if (type.type !== 'custom') {
        if (!filters.types[type.type]) {
          return false
        }
      } else if (!type.customToDoType) {
        return false
      } else if (!filters.types[type.customToDoType.id]) {
        return false
      }
    }

    // if (
    //   Object.keys(filters.types).length !== 0 &&
    //   !(todo.type.type !== 'custom'
    //     ? filters.types[todo.type.type]
    //     : filters.types[todo.type.customToDoType.id])
    // ) {
    //   return false
    // }

    const firstPlantId = todo.plants?.[0]?.id
    if (
      Object.keys(filters.plants).length !== 0 &&
      (firstPlantId === undefined || !filters.plants[firstPlantId])
    ) {
      return false
    }

    return true
  })

  const sortedFilteredToDos = sortToDo(filteredToDos, toDoSortingField, sortingDirection)

  useEffect(() => {
    setUserToDos((current) =>
      current.map((todo) => {
        const type = todo.type
        if (!type || type.type !== 'custom') {
          return todo
        }

        // a type the user has since deleted keeps its reference but loses its name
        const orphaned: ToDoType = {type: 'custom', customToDoType: null}
        const customToDoType = type.customToDoType
        if (!customToDoType) {
          return {...todo, type: orphaned}
        }

        const newType = userToDoTypes.find((x) => x.customToDoType?.id === customToDoType.id)

        return {...todo, type: newType ? newType : orphaned}
      })
    )
  }, [userToDoTypes])

  useEffect(() => {
    setUserToDos(unfinishedToDos)
  }, [unfinishedToDos])

  const refAddToDo = useRef<any>(null)
  const refToDoFilters = useRef<any>(null)
  const refToDoList = useRef<any>(null)

  const [openTour, setOpenTour] = useState<boolean>(false)

  const steps: TourProps['steps'] = [
    {
      title: intl.formatMessage({id: 'todoPage'}),
      description: intl.formatMessage({id: 'todoPageTourDescriptionAll'}),
      target: null,
    },
    {
      title: intl.formatMessage({id: 'newTaskAdding'}),
      description: intl.formatMessage({id: 'todoPageTourDescriptionAdd'}),
      target: () => refAddToDo.current!,
    },
    {
      title: intl.formatMessage({id: 'todoLists'}),
      description: intl.formatMessage({id: 'todoPageTourDescriptionList'}),
      target: null, //() => refToDoList.current!,
    },
    {
      title: intl.formatMessage({id: 'filtersAndSorting'}),
      description: intl.formatMessage({id: 'todoPageTourDescriptionFilter'}),
      target: null, //() => refToDoFilters.current!,
    },
    {
      title: intl.formatMessage({id: 'taskEditing'}),
      description: intl.formatMessage({id: 'todoPageTourDescriptionEdit'}),
      placement: 'left',
      target: null,
    },
    {
      title: intl.formatMessage({id: 'taskFinishing'}),
      description: (
        <div>
          <p>{intl.formatMessage({id: 'todoPageTourDescriptionEndTask1'})}</p>
          <p>{intl.formatMessage({id: 'todoPageTourDescriptionEndTask2'})}</p>
          <p>{intl.formatMessage({id: 'todoPageTourDescriptionEndTask3'})}</p>
        </div>
      ),
      placement: 'left',
      target: null,
    },
    {
      title: intl.formatMessage({id: 'taskDeleting'}),
      description: intl.formatMessage({id: 'todoPageTourDescriptionDeleteTask'}),
      target: null,
    },
    {
      title: intl.formatMessage({id: 'plantPageWorkWithTodo'}),
      description: intl.formatMessage({id: 'todoPageTourDescriptionPlantPage'}),
      target: null,
    },
  ]

  return (
    <div>
      <Row style={{justifyContent: 'space-between', alignItems: 'baseline'}}>
        <h2>{intl.formatMessage({id: 'myToDos'})}</h2>
        <Button onClick={() => setOpenTour(true)}>
          {intl.formatMessage({id: 'todoPageTour'})}
        </Button>
      </Row>

      <Row className="todo__new" ref={refAddToDo}>
        <ToDoPageNewToDo
          plantedPlantsNamesWithId={plantedPlantsNamesWithId}
          toDoTypes={userToDoTypes}
          userPlantCollections={userPlantCollections}
          userPlantCollectionsWithPlantsIds={userPlantCollectionsWithPlantsIds}
        ></ToDoPageNewToDo>
      </Row>
      <Divider></Divider>

      <Row ref={refToDoFilters}>
        <ToDoPageFilters
          plantedPlantsNamesWithId={plantedPlantsNamesWithId}
          userToDoTypes={userToDoTypes}
          filters={filters}
          setFilters={(newFilters) => {
            newFilters.plants = removeFalsyKeys(newFilters.plants)
            newFilters.types = removeFalsyKeys(newFilters.types)

            setFilters(newFilters)
          }}
          sortingField={toDoSortingField}
          setSortingField={setToDoSortingField}
          sortingDirection={sortingDirection}
          setSortingDirection={setSortingDirection}
        ></ToDoPageFilters>
      </Row>
      <Divider></Divider>

      <Row className="todo__list" ref={refToDoList}>
        <ToDoPageToDoCardsList
          unfinishedToDos={sortedFilteredToDos}
          plantedPlantsNamesWithId={plantedPlantsNamesWithId}
        ></ToDoPageToDoCardsList>
      </Row>
      <Tour
        open={openTour}
        onClose={() => setOpenTour(false)}
        steps={steps}
        indicatorsRender={(current, total) => (
          <span>
            {current + 1} / {total}
          </span>
        )}
      />
      <PlantModal routeWhenClosed={`/todos`} />
      <ToDoModal
        routeWhenClosed={`/todos`}
        unfinishedToDos={sortedFilteredToDos}
        toDoTypes={userToDoTypes}
        userEventTypes={userJournalEventTypes}
      />
    </div>
  )
}
